import { zodResolver } from '@hookform/resolvers/zod';
import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FilterBy, useProfiles } from '../../contexts/ProfileContext';

import { AnimateSpin } from '@/components/AnimateSpin';
import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';

const formSchema = z.object({
  searchTerm: z.string(),
  filterBy: z.nativeEnum(FilterBy).default(FilterBy.NAME),
});

type FormData = z.infer<typeof formSchema>;

export function SearchForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Profiles.components.SearchForm',
  });

  const { search, resetSearch } = useProfiles();

  const {
    formState: { isValid, errors, isSubmitting },
    control,
    register,
    handleSubmit,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      filterBy: FilterBy.NAME,
    },
  });

  const searchTerm = watch('searchTerm');

  const onSubmit = handleSubmit((data) => {
    search(data.searchTerm, data.filterBy);
  });

  useEffect(() => {
    if (!searchTerm) {
      resetSearch();
    }
  }, [searchTerm, resetSearch]);

  return (
    <form className="flex gap-3" onSubmit={onSubmit}>
      <FormGroup
        error={{ message: errors.searchTerm?.message, font: 'sm' }}
        className="flex-1"
      >
        <Input
          id="searchTerm"
          className="w-full"
          placeholder={t('searchTermPlaceholder')}
          {...register('searchTerm')}
        />
      </FormGroup>

      <div className="flex gap-2">
        <FormGroup error={{ message: errors.filterBy?.message, font: 'sm' }}>
          <Controller
            name="filterBy"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={(value) => field.onChange(value)}
                defaultValue={field.value}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={t('filterBy.placeholder')} />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value={FilterBy.NAME}>
                    {t('filterBy.name')}
                  </SelectItem>

                  <SelectItem value={FilterBy.EMAIL}>
                    {t('filterBy.email')}
                  </SelectItem>

                  <SelectItem value={FilterBy.ID}>
                    {t('filterBy.id')}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormGroup>

        <Button
          type="submit"
          className="flex items-center gap-2"
          disabled={!isValid}
        >
          <Search className={`h-4 w-4 ${isSubmitting ? 'animate-spin' : ''}`} />
          {isSubmitting && <AnimateSpin />}
          {!isSubmitting && t('submitButton')}
        </Button>
      </div>
    </form>
  );
}
